import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { prepareDemo, WARM_PAGES } from '../lib/demoWarmup';
import { getDemoReady } from '../lib/pageCache';

// Module-level run state so the progress survives the launcher panel closing/reopening.
let state = { running: false, progress: null };
const subs = new Set();
const set = (patch) => { state = { ...state, ...patch }; subs.forEach((fn) => fn(state)); };

export async function runPrepareDemo(orgId) {
  if (state.running) return;
  set({ running: true, progress: { current: WARM_PAGES[0], done: [] } });
  try {
    const results = await prepareDemo({ organizationId: orgId, onProgress: (p) => set({ progress: p }) });
    const failed = results.filter((r) => !r.ok).length;
    const views = results.reduce((a, r) => a + r.writes, 0);
    if (failed) toast.warning(`Demo prepared with ${failed} page${failed > 1 ? 's' : ''} still loading — open them once before the call.`);
    else toast.success('Demo ready — pages will paint instantly for 24h', { description: `${views} views cached from fresh data.` });
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Could not prepare the demo');
  } finally {
    set({ running: false });
    setTimeout(() => set({ progress: null }), 8000);
  }
}

export function useDemoWarmup() {
  const [s, setS] = useState(state);
  useEffect(() => { subs.add(setS); return () => subs.delete(setS); }, []);
  return s;
}

export const agoLabel = (at) => {
  const m = Math.max(0, Math.round((Date.now() - at) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return `${h} hr${h > 1 ? 's' : ''} ago`;
};

// Latest Prepare-demo marker (null when never run, wiped by Resync, or older than 24h); ticks every minute.
export function useDemoReady() {
  const [info, setInfo] = useState(getDemoReady);
  const [, tick] = useState(0);
  useEffect(() => {
    const refresh = () => setInfo(getDemoReady());
    window.addEventListener('demo-ready:change', refresh);
    window.addEventListener('storage', refresh);
    const t = setInterval(() => { refresh(); tick((n) => n + 1); }, 60000);
    return () => { window.removeEventListener('demo-ready:change', refresh); window.removeEventListener('storage', refresh); clearInterval(t); };
  }, []);
  return info;
}

// ---- Auto warm-up: per-browser schedule (the copies live in this browser, so only it can build them).
const AUTO_KEY = 'lei_auto_warm';
const AUTO_LAST_KEY = 'lei_auto_warm_last';
const readAuto = () => { try { return { enabled: false, hour: 7, ...(JSON.parse(localStorage.getItem(AUTO_KEY) || '{}')) }; } catch { return { enabled: false, hour: 7 }; } };
const dayKey = (d = new Date()) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;

export function useAutoWarmSettings() {
  const [cfg, setCfg] = useState(readAuto);
  useEffect(() => {
    const refresh = () => setCfg(readAuto());
    window.addEventListener('auto-warm:change', refresh);
    window.addEventListener('storage', refresh);
    return () => { window.removeEventListener('auto-warm:change', refresh); window.removeEventListener('storage', refresh); };
  }, []);
  const update = (patch) => {
    try { localStorage.setItem(AUTO_KEY, JSON.stringify({ ...readAuto(), ...patch })); } catch { /* noop */ }
    window.dispatchEvent(new Event('auto-warm:change'));
  };
  return [cfg, update];
}

export const nextAutoRunLabel = (hour) => {
  const now = new Date();
  const due = new Date(now); due.setHours(hour, 0, 0, 0);
  const ranToday = localStorage.getItem(AUTO_LAST_KEY) === dayKey();
  const tomorrow = ranToday || now >= due;
  const hh = `${String(hour).padStart(2, '0')}:00`;
  return tomorrow ? `tomorrow ${hh}` : `today ${hh}`;
};

// Runs Prepare demo once per day at/after the chosen hour: live at HH:00 if a tab is open,
// otherwise the first time Mission Control is opened later that day (catch-up).
export function useAutoWarmupScheduler(orgId) {
  useEffect(() => {
    const check = () => {
      const cfg = readAuto();
      if (!cfg.enabled || state.running || !orgId) return;
      const now = new Date();
      const due = new Date(now); due.setHours(cfg.hour, 0, 0, 0);
      if (now < due) return;
      const today = dayKey();
      if (localStorage.getItem(AUTO_LAST_KEY) === today) return;
      const ready = getDemoReady();
      localStorage.setItem(AUTO_LAST_KEY, today); // claim the slot first so other tabs don't double-run
      if (ready && ready.at >= due.getTime()) return; // a manual run already warmed today
      runPrepareDemo(orgId);
    };
    const t = setTimeout(check, 5000);
    const i = setInterval(check, 60000);
    return () => { clearTimeout(t); clearInterval(i); };
  }, [orgId]);
}
